import { useCallback, useEffect, useState } from "react";
import {
  getDeferredEvent,
  clearDeferredEvent,
  wasInstalledEventSeen,
  subscribeInstallPrompt,
} from "utils/installPromptCapture";

/**
 * Everything the install prompt needs to know about how this browser installs.
 *
 * There is no single API for this. Chromium fires `beforeinstallprompt` and
 * hands over an event we can trigger later; every WebKit browser installs only
 * through a menu the user opens themselves. So the hook reports a `method` and
 * the UI either offers a button or shows steps.
 */

const DISMISS_KEY = "healthdesk.installPrompt.dismissed";

// After this many dismissals the nudge stops appearing for good. The entry in
// the nav drawer is always there, so nothing becomes unreachable.
const MAX_DISMISSALS = 3;
const SNOOZE_DAYS = 30;

/** localStorage throws in private windows and when site data is blocked. */
const readDismissal = () => {
  try {
    return JSON.parse(window.localStorage.getItem(DISMISS_KEY)) || null;
  } catch {
    return null;
  }
};

const writeDismissal = (value) => {
  try {
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify(value));
  } catch {
    // A viewer who cannot persist the dismissal sees the nudge again next
    // session. Annoying, not broken.
  }
};

/** True once the app is running from the home screen rather than a browser tab. */
export const isRunningInstalled = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS predates the display-mode media query and still reports this instead.
    window.navigator.standalone === true
  );
};

export const detectInstallMethod = (
  ua = window.navigator.userAgent,
  platform = window.navigator.platform,
  maxTouchPoints = window.navigator.maxTouchPoints,
) => {
  // An embedded webview (the in-app browser inside Facebook, Instagram, and
  // friends) cannot add anything to the home screen at all. The only useful
  // advice is to reopen the page in a real browser.
  if (/FBAN|FBAV|Instagram|Line\/|MicroMessenger|Snapchat/i.test(ua)) {
    return "in-app-browser";
  }

  // iPadOS reports itself as a Mac, so the touch check is what separates a
  // tablet from a desktop.
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (platform === "MacIntel" && maxTouchPoints > 1);

  if (isIOS) {
    // Every iOS browser is WebKit underneath, but the Share control sits in a
    // different place in each one, so the steps differ.
    if (/CriOS/i.test(ua)) return "ios-chrome";
    if (/EdgiOS|FxiOS|DuckDuckGo/i.test(ua)) return "ios-other";
    return "ios-safari";
  }

  // Desktop Safari installs through the File menu, with no event to listen for.
  if (/^((?!chrome|android|crios|fxios).)*safari/i.test(ua)) {
    return "macos-safari";
  }

  return "unsupported";
};

/**
 * Brave's iOS UA is indistinguishable from Safari's by design (Brave strips
 * its own identity to avoid being singled out for fingerprinting/blocking),
 * so `detectInstallMethod` alone always mislabels it "ios-safari" — wrong
 * menu, wrong steps. Brave exposes this opt-in API instead, on every
 * platform it ships, purely so sites can tell the two apart when it matters.
 */
const isBraveBrowser = async () => {
  try {
    return Boolean(await window.navigator.brave?.isBrave?.());
  } catch {
    return false;
  }
};

export const useInstallApp = () => {
  // installPromptCapture.js listens from src/index.js, before this hook could
  // ever mount, so the event may already be sitting there on first render.
  const [deferredEvent, setDeferredEvent] = useState(getDeferredEvent);
  const [installed, setInstalled] = useState(
    () => isRunningInstalled() || wasInstalledEventSeen(),
  );
  const [method, setMethod] = useState(() => {
    if (typeof window === "undefined") return "unsupported";
    return getDeferredEvent() ? "prompt" : detectInstallMethod();
  });

  useEffect(() => {
    const sync = () => {
      const event = getDeferredEvent();
      if (event) {
        setDeferredEvent(event);
        setMethod("prompt");
      }
      if (wasInstalledEventSeen()) {
        setInstalled(true);
        setDeferredEvent(null);
      }
    };
    sync();
    return subscribeInstallPrompt(sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    isBraveBrowser().then((isBrave) => {
      if (!cancelled && isBrave) {
        // Only correct the UA misdetection — never override a real Chromium
        // "prompt" method, which Android Brave gets the normal way.
        setMethod((current) => (current === "ios-safari" ? "ios-other" : current));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Fires the browser's own install dialog. Only ever available on Chromium,
   * and only once per captured event, so the event is cleared either way.
   */
  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return false;
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    clearDeferredEvent();
    setDeferredEvent(null);
    return outcome === "accepted";
  }, [deferredEvent]);

  const dismissal = readDismissal();

  const dismiss = useCallback(() => {
    const current = readDismissal();
    writeDismissal({
      count: (current?.count || 0) + 1,
      at: Date.now(),
    });
  }, []);

  // Has the user put this away recently, or often enough to mean it?
  const snoozed = Boolean(
    dismissal &&
    ((dismissal.count || 0) >= MAX_DISMISSALS ||
      Date.now() - (dismissal.at || 0) < SNOOZE_DAYS * 24 * 60 * 60 * 1000),
  );

  const supported = method !== "unsupported" && method !== "in-app-browser";

  // Installing is offered on phones and tablets only, by both the nudge and the
  // nav drawer entry. Desktop Chrome and macOS Safari can install the app too,
  // but "Add to home screen" means nothing on a desktop, and a dock icon is not
  // something anyone there asked for. A coarse primary pointer is the check rather than the user
  // agent: a touchscreen laptop with a mouse still reports a fine pointer.
  const isHandheld =
    typeof window !== "undefined" &&
    window.matchMedia?.("(pointer: coarse)").matches;

  return {
    /** How this browser installs: "prompt", "ios-safari", "macos-safari", ... */
    method,
    /** Already on the home screen, so there is nothing to offer. */
    installed,
    /** This browser can install the app one way or another. */
    supported,
    /** Chromium handed us an event, so a single button will do it. */
    canPromptDirectly: Boolean(deferredEvent),
    /** A touch device, where installing to the home screen makes sense. */
    isHandheld,
    promptInstall,
    /** The unprompted nudge is allowed to appear. Handheld only. */
    nudgeAllowed: supported && isHandheld && !installed && !snoozed,
    dismiss,
  };
};

export default useInstallApp;
